import { useState } from 'react';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import { Button, Group, Select, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import { Link, useNavigate } from 'react-router';
import { SOZLESME_DURUMU_ETIKETI } from '@isg/validation';
import {
  BOS_YUKLENICI_SUZGECI,
  etkinYukleniciSuzgeciSayisi,
  useCiktiIndir,
  useEvrakPaketleri,
  useYukleniciAc,
  useYukleniciListesi,
  yukleniciSuzgecSorgusu,
  type YukleniciSuzgeci,
  useKapilar,
} from '../api/queries';
import { gunGirdisiIso, tarihGoster } from '../lib/format';
import { QueryStatus } from './parts';
import { SahaGirisiRozeti } from './evrak-parts';
import {
  Rozet,
  SatirEylemleri,
  Sayfalama,
  SolukSatir,
  SuzgecGostergesi,
  KapiSebebi,
} from '../sozlesme';
import { ANLAM } from '../theme';

/**
 * YÜKLENİCİ LİSTESİ (SERTLEŞTİRME TUR 6 / Y1 — P4: sunucuda süzgeç +
 * sayfalama + görünür toplam; E1: üstte tetikleyici + soluk satır).
 *
 * Keşif: firma açma ucu 39 gün, evrak kararı 22 gün EKRANSIZDI — firma ve
 * evrak yalnız seed/API ile var oluyordu. Bu ekran ilk iç yüzeydir.
 *
 * ⚠️ SAHA GİRİŞİ ROZETİ SUNUCUDAN (`sahaGirisi` — tek yüklem); ekran tarih
 * hesaplamaz. Eski ekran `saha_giris_onay` KOLONUNU çiziyordu (28 gün iki
 * cevap). Yetki bayrakları da sunucudan (`yetkiler`) — istemci rol listesi
 * (`DAVET_ACABILEN`) söküldü.
 */
const SAYFA_BOYUTU = 25;
const KOLON_SAYISI = 5;

export function YuklenicilerScreen(): React.JSX.Element {
  const navigate = useNavigate();
  const [suzgec, setSuzgec] = useState<YukleniciSuzgeci>(BOS_YUKLENICI_SUZGECI);
  const [aramaGirdisi, setAramaGirdisi] = useState('');
  const [solukAcik, setSolukAcik] = useState(false);
  const liste = useYukleniciListesi(suzgec);
  const indir = useCiktiIndir();
  // P13-b (madde 225): firma açma kapısı SUNUCUDAN (`YUKLENICI_YAZMA` — holding + şirket yön.).
  const kapilar = useKapilar();
  const acabilir = kapilar.data?.yukleniciKapisi.yazabilir ?? false;

  return (
    <Stack>
      <Group justify="space-between" align="flex-end">
        <Title order={3}>Yükleniciler</Title>
        <Group gap="xs">
          {acabilir && (
            <Button size="xs" data-testid="firma-ac-tetik" onClick={() => setSolukAcik((v) => !v)}>
              Firma aç
            </Button>
          )}
          {kapilar.data && (
            <KapiSebebi
              durum={kapilar.data.yukleniciKapisi}
              nesne="Yüklenici firmayı"
              fiil="açar"
              testId="firma-ac-yazamama-sebebi"
            />
          )}
          <Button
            size="xs"
            variant="default"
            component={Link}
            to="/yukleniciler/evrak-paketleri"
            data-testid="evrak-paketleri-ac"
          >
            Evrak paketleri
          </Button>
          <Button
            size="xs"
            variant="default"
            data-testid="evrak-durumu-indir"
            loading={indir.isPending}
            onClick={() =>
              indir.mutate({
                yol: `/api/contractors/evrak-durumu/export${yukleniciSuzgecSorgusu(suzgec, false)}`,
                yedekAd: 'yuklenici-evrak-durumu.xlsx',
              })
            }
          >
            Evrak durumu (Excel)
          </Button>
        </Group>
      </Group>
      <Group gap="xs" align="flex-end">
        <TextInput
          size="xs"
          placeholder="Firma adı"
          data-testid="yuklenici-arama"
          value={aramaGirdisi}
          onChange={(e) => setAramaGirdisi(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setSuzgec({ ...suzgec, arama: aramaGirdisi, sayfa: 1 });
          }}
          onBlur={() => setSuzgec({ ...suzgec, arama: aramaGirdisi, sayfa: 1 })}
        />
        <Select
          size="xs"
          data-testid="suzgec-saha-girisi"
          data={[
            { value: 'hepsi', label: 'Tüm saha girişi durumları' },
            { value: 'gecerli', label: 'Saha girişi geçerli' },
            { value: 'gecersiz', label: 'Saha girişi geçersiz' },
          ]}
          value={suzgec.sahaGirisi ?? 'hepsi'}
          onChange={(deger) =>
            setSuzgec({
              ...suzgec,
              sahaGirisi: deger === 'gecerli' || deger === 'gecersiz' ? deger : null,
              sayfa: 1,
            })
          }
        />
        <Select
          size="xs"
          data-testid="suzgec-sozlesme"
          data={[
            { value: 'hepsi', label: 'Tüm sözleşmeler' },
            { value: 'sabit', label: SOZLESME_DURUMU_ETIKETI.sabit },
            { value: 'suren', label: SOZLESME_DURUMU_ETIKETI.suren },
            { value: 'bitmis', label: SOZLESME_DURUMU_ETIKETI.bitmis },
          ]}
          value={suzgec.sozlesme ?? 'hepsi'}
          onChange={(deger) =>
            setSuzgec({
              ...suzgec,
              sozlesme: deger === 'sabit' || deger === 'suren' || deger === 'bitmis' ? deger : null,
              sayfa: 1,
            })
          }
        />
        <SuzgecGostergesi
          etkinSayisi={etkinYukleniciSuzgeciSayisi(suzgec)}
          onTemizle={() => {
            setAramaGirdisi('');
            setSuzgec(BOS_YUKLENICI_SUZGECI);
          }}
        />
      </Group>
      <QueryStatus queries={[liste]} />
      {liste.data && (
        <>
          <Table striped highlightOnHover data-testid="yuklenici-listesi">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Firma</Table.Th>
                <Table.Th>Saha girişi</Table.Th>
                <Table.Th>Sözleşme</Table.Th>
                <Table.Th>Sorumlu</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {solukAcik && acabilir && (
                <FirmaSolukSatiri
                  kolonSayisi={KOLON_SAYISI - 1}
                  onAcildi={(id) => {
                    setSolukAcik(false);
                    navigate(`/yukleniciler/${id}`);
                  }}
                />
              )}
              {liste.data.satirlar.map((firma) => (
                <Table.Tr key={firma.id} data-testid="yuklenici-karti">
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {firma.ad}
                    </Text>
                    {firma.vergiNo && (
                      <Text size="xs" c="dimmed">
                        VKN {firma.vergiNo}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <SahaGirisiRozeti sahaGirisi={firma.sahaGirisi} />
                  </Table.Td>
                  <Table.Td>
                    <Rozet anlam={firma.sozlesmeDurumu === 'bitmis' ? 'dikkat' : 'bilgi'}>
                      {SOZLESME_DURUMU_ETIKETI[firma.sozlesmeDurumu]}
                    </Rozet>
                    {firma.sozlesmeBitis && (
                      <Text size="xs" c="dimmed">
                        {tarihGoster(firma.sozlesmeBitis)}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>{firma.sorumluPersonAd ?? '—'}</Table.Td>
                  <Table.Td>
                    <SatirEylemleri
                      birincil={{
                        etiket: 'Aç',
                        to: `/yukleniciler/${firma.id}`,
                        testId: 'yuklenici-ac',
                      }}
                    />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Sayfalama
            sayfa={suzgec.sayfa}
            sayfaBoyutu={SAYFA_BOYUTU}
            toplam={liste.data.toplam}
            onDegis={(sayfa) => setSuzgec({ ...suzgec, sayfa })}
          />
        </>
      )}
    </Stack>
  );
}

/** E1 — firma açma soluk satırı (ad · vergi no · sözleşme bitişi); künye detayda tamamlanır. */
function FirmaSolukSatiri({
  kolonSayisi,
  onAcildi,
}: {
  kolonSayisi: number;
  onAcildi: (id: string) => void;
}): React.JSX.Element {
  const ac = useYukleniciAc();
  const paketler = useEvrakPaketleri();
  const [ad, setAd] = useState('');
  const [vergiNo, setVergiNo] = useState('');
  const [bitis, setBitis] = useState('');
  // K-102b — paket SEÇİLİR; varsayılan atanmaz (sessiz boşluk açılmaz).
  const [paketId, setPaketId] = useState<string | null>(null);
  // P12 (madde 224) — kaydedilmemiş firma satırı çıkışta uyarır (yönlendirici tek noktada).
  useKaydedilmemisIcerik(ad !== '' || vergiNo !== '' || bitis !== '' ? 1 : 0, 'firma satırı');
  const bitisIso = bitis ? gunGirdisiIso(bitis) : null;
  const kaydedilebilir =
    ad.trim().length > 0 && (bitis === '' || bitisIso !== null) && paketId !== null;
  return (
    <SolukSatir
      testId="firma-soluk-satir"
      dokunuldu={ad !== '' || vergiNo !== '' || bitis !== ''}
      kaydedilebilir={kaydedilebilir}
      kaydediliyor={ac.isPending}
      onKaydet={() => {
        if (!paketId) return;
        ac.mutate(
          {
            ad: ad.trim(),
            vergiNo: vergiNo.trim() || null,
            sozlesmeBitis: bitisIso ? bitis : null, // gün alanı — damga değil (şema isoDate)
            evrakPaketiId: paketId,
          },
          { onSuccess: (firma) => onAcildi(firma.id) },
        );
      }}
      kolonSayisi={kolonSayisi}
      ikinciKatman={
        ac.isError ? (
          <Text size="sm" c={ANLAM.engel} role="alert" data-testid="firma-ac-hata">
            {ac.error.message}
          </Text>
        ) : ad.trim().length > 0 && paketId === null ? (
          <Text size="xs" c="dimmed" data-testid="firma-ac-sebep">
            Firma için evrak paketi seçilmeli
          </Text>
        ) : undefined
      }
    >
      <Table.Td>
        <TextInput
          size="xs"
          placeholder="Firma adı"
          data-testid="firma-ad"
          value={ad}
          onChange={(e) => setAd(e.currentTarget.value)}
        />
      </Table.Td>
      <Table.Td>
        <TextInput
          size="xs"
          placeholder="Vergi no"
          data-testid="firma-vergi-no"
          value={vergiNo}
          onChange={(e) => setVergiNo(e.currentTarget.value)}
        />
      </Table.Td>
      <Table.Td>
        <TextInput
          size="xs"
          type="date"
          placeholder="Sözleşme bitişi (boş = sabit)"
          data-testid="firma-sozlesme-bitis"
          value={bitis}
          onChange={(e) => setBitis(e.currentTarget.value)}
        />
      </Table.Td>
      <Table.Td>
        <Select
          size="xs"
          placeholder="Evrak paketi"
          aria-label="Evrak paketi"
          data-testid="firma-evrak-paketi"
          comboboxProps={{ withinPortal: true }}
          data={(paketler.data ?? []).map((p) => ({ value: p.id, label: p.ad }))}
          value={paketId}
          onChange={setPaketId}
        />
      </Table.Td>
    </SolukSatir>
  );
}
